import axios from 'axios';
import { getAuth, signOut } from 'firebase/auth';

/**
 * Flag global para evitar múltiplos redirecionamentos quando várias
 * requisições retornam SESSION_EXPIRED simultaneamente.
 */
let sessionExpiredHandled = false;

/**
 * Trata sessão expirada (401 + SESSION_EXPIRED): faz signOut do Firebase,
 * limpa storage e redireciona para a tela de login do subdomínio atual.
 */
async function handleSessionExpired() {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  try {
    console.warn('🔒 [AUTH] Sessão expirada detectada — forçando logout e redirect');
    try { await signOut(getAuth()); } catch (e) { /* ignore */ }
    try { localStorage.removeItem('cbtkd_auth_token'); } catch (e) { /* ignore */ }
    try { localStorage.removeItem('cbtkd_user_data'); } catch (e) { /* ignore */ }
    try { localStorage.removeItem('cbtkd_login_time'); } catch (e) { /* ignore */ }
    try { localStorage.removeItem('cbtkd_refresh_token'); } catch (e) { /* ignore */ }
    if (typeof window !== 'undefined') {
      alert('Sua sessão expirou. Faça login novamente para continuar.');
      window.location.href = '/Login';
    }
  } catch (e) {
    console.error('[AUTH] Erro ao tratar sessão expirada:', e);
  }
}

// Interceptor global de respostas: captura 401 com code SESSION_EXPIRED
// vindo de qualquer chamada axios e dispara logout/redirect.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      const data = error?.response?.data;
      const code = data?.code || (typeof data === 'string' ? null : null);
      if (status === 401 && code === 'SESSION_EXPIRED') {
        handleSessionExpired();
      }
    } catch (e) { /* ignore */ }
    return Promise.reject(error);
  }
);

/**
 * Serviço para fazer requisições autenticadas para a API
 * Adiciona automaticamente o token Bearer quando o usuário está logado
 */
class AuthenticatedApiService {
  
  constructor() {
    this.baseURL = import.meta.env.VITE_BACKEND_API_URL;
    this.auth = getAuth();
    
    // 🔍 DEBUG: Log da URL base ao construir o serviço
    // console.log('[AuthenticatedApiService] 🌐 baseURL configurada:', this.baseURL);
    if (!this.baseURL || this.baseURL === 'undefined') {
      console.error('[AuthenticatedApiService] ⚠️ VITE_BACKEND_API_URL não está definida!');
    }
  }

  /**
   * Trata sessão expirada manualmente (para uso em fetch() direto, fora do axios).
   * @param {Response} response - resposta do fetch
   * @returns {Promise<boolean>} true se a resposta era SESSION_EXPIRED e foi tratada
   */
  static async handleFetchSessionExpired(response) {
    if (!response || response.status !== 401) return false;
    try {
      const cloned = response.clone();
      const data = await cloned.json().catch(() => null);
      if (data && data.code === 'SESSION_EXPIRED') {
        await handleSessionExpired();
        return true;
      }
    } catch (e) { /* ignore */ }
    return false;
  }

  /**
   * Obtém o token de autenticação do usuário atual
   * @returns {Promise<string|null>} Token JWT ou null se não autenticado
   */
  async getAuthToken() {
    try {
      const user = this.auth.currentUser;
      if (user) {
        return await user.getIdToken();
      }
      return null;
    } catch (error) {
      console.warn('Erro ao obter token de autenticação:', error);
      return null;
    }
  }

  /**
   * Cria headers com autenticação se disponível
   * @returns {Promise<Object>} Headers para a requisição
   */
  async createHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };

    const token = await this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  /**
   * Faz uma requisição POST com autenticação automática
   * @param {string} endpoint - Endpoint da API (ex: "/api/gnet/createpix")
   * @param {Object} data - Dados a serem enviados
   * @param {Object} options - Opções adicionais do axios
   * @returns {Promise} Resposta da requisição
   */
  async post(endpoint, data, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.createHeaders();
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    };
    
    return axios.post(url, data, config);
  }

  /**
   * Faz uma requisição GET com autenticação automática
   * @param {string} endpoint - Endpoint da API
   * @param {Object} options - Opções adicionais do axios
   * @returns {Promise} Resposta da requisição
   */
  async get(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.createHeaders();
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    };

    console.log(`[API] GET ${endpoint}`, { hasAuth: !!headers['Authorization'] });
    
    return axios.get(url, config);
  }

  /**
   * Faz uma requisição PUT com autenticação automática
   * @param {string} endpoint - Endpoint da API
   * @param {Object} data - Dados a serem enviados
   * @param {Object} options - Opções adicionais do axios
   * @returns {Promise} Resposta da requisição
   */
  async put(endpoint, data, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.createHeaders();
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    };

    console.log(`[API] PUT ${endpoint}`, { data, hasAuth: !!headers['Authorization'] });
    
    return axios.put(url, data, config);
  }

  /**
   * Faz uma requisição DELETE com autenticação automática
   * @param {string} endpoint - Endpoint da API
   * @param {Object} options - Opções adicionais do axios
   * @returns {Promise} Resposta da requisição
   */
  async delete(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.createHeaders();
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...(options.headers || {})
      }
    };

    console.log(`[API] DELETE ${endpoint}`, { hasAuth: !!headers['Authorization'] });
    
    return axios.delete(url, config);
  }

  /**
   * Método compatível para o código existente que usa axios diretamente
   * Faz uma requisição POST mantendo compatibilidade com o código atual
   * @param {string} url - URL completa ou endpoint relativo
   * @param {Object} data - Dados a serem enviados
   * @param {Object} options - Opções adicionais
   * @returns {Promise} Resposta da requisição
   */
  /**
   * POST autenticado com a interface do `fetch` nativo.
   *
   * Existe para os fallbacks que já estavam escritos em `fetch` cru — e sem
   * token, que era o problema: `Generic.service` e `GenericVinculado.service`
   * caem na API quando o acesso direto ao Firestore falha, e /api/generic lê com
   * Admin SDK, por cima das Security Rules. Devolver um objeto com `ok`/`json()`
   * deixa o token entrar sem reescrever os pontos de uso.
   *
   * @param {string} endpoint
   * @param {Object} data
   * @returns {Promise<{ok: boolean, status: number, json: () => Promise<any>}>}
   */
  async fetch(endpoint, data) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.createHeaders();
    const resposta = await window.fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data),
    });
    return resposta;
  }

  async makeCompatibleRequest(url, data, options = {}) {
    // Se a URL já for completa (contém http), usar como está
    // Senão, considerar como endpoint relativo
    const isFullUrl = url.startsWith('http');
    const endpoint = isFullUrl ? url.replace(this.baseURL, '') : url;
    
    return this.post(endpoint, data, options);
  }
}

export { handleSessionExpired, AuthenticatedApiService };
export default new AuthenticatedApiService();
